/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataExplorerContextV2 } from "@depmap/types";

function makeMockEnabledFeatures() {
  (window as any).enabledFeaturesOverrides = {
    elara: false,
  };

  return new Proxy((window as any).enabledFeaturesOverrides, {
    get(obj, prop) {
      if (!(prop in obj)) {
        window.console.warn(
          `enabledFeatures.${String(prop)} is defaulting to true.`,
          `To override this, set \`(window as any).enabledFeaturesOverrides.${String(
            prop
          )} = false\`.`
        );
        return true;
      }

      return obj[prop];
    },
  });
}

// This module is also bundled for Node (see embed-frontend/webpack.node.js),
// where there is no `window` or `document`. Everything defined at module scope
// below must tolerate that.
const hasDom = typeof window !== "undefined" && typeof document !== "undefined";

// In the Portal, this is defined here:
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/portal-backend/depmap/templates/nav_footer/layout.html#L88
// In Elara, it's defined here:
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/frontend/packages/elara-frontend/webpack.common.js#L34-L36
export const depmapContactUrl: string = hasDom
  ? (window as any).depmapContactUrl
  : "";

// This is injected into the HTML by the Portal backend:
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/portal-backend/depmap/templates/nav_footer/layout.html#L88
// In Elara, it's instead "baked in" as a hardcoded feature list at build time:
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/frontend/packages/elara-frontend/webpack.common.js#L29-L33
// If it doesn't exist on `window` then we assume we're running in a test
// environment (like Jest or Storybook) and return a mock.
// Outside a browser there is nothing to mock or detect, so no features are on.
export const enabledFeatures: Record<string, boolean> = hasDom
  ? (window as any).enabledFeatures || makeMockEnabledFeatures()
  : {};

export const isPortal: boolean =
  hasDom && Boolean(document.getElementById("webpack-config"));

function extractUrlPrefixFromWebpackConfig() {
  if (!hasDom) {
    return "";
  }

  const element = document.getElementById("webpack-config");
  let urlPrefix = "";

  try {
    const config = JSON.parse(element?.textContent || "{}");

    if (config && typeof config.rootUrl === "string") {
      urlPrefix = config.rootUrl.trim();
    } else {
      window.console.error(
        "Invalid webpack-config: Missing or malformed rootUrl."
      );
    }
  } catch (e) {
    window.console.error("Failed to parse webpack-config:", e);
  }

  return urlPrefix;
}

export const getUrlPrefix = () => {
  if (process.env.JEST_WORKER_ID) {
    return "";
  }

  if (enabledFeatures.elara) {
    // Detect when Elara is being served behind the DepMap Portal proxy.
    if (window.location.pathname.includes("/breadbox/elara")) {
      return window.location.pathname.replace(/\/elara\/.*$/, "");
    }

    return "";
  }

  if (enabledFeatures.embed) {
    // Detect when Embed is being served behind the DepMap Portal proxy.
    if (window.location.pathname.includes("/breadbox/embed")) {
      return window.location.pathname.replace(/\/embed\/.*$/, "");
    }

    return "";
  }

  return extractUrlPrefixFromWebpackConfig();
};

export function toPortalLink(relativeUrl: string) {
  if (!isPortal) {
    throw new Error("Portal links are not supported in this environment!");
  }

  const trimmed = relativeUrl.trim().replace(/^\//, "");
  const [path, queryAndFragment] = trimmed.split(/(?=[?#])/); // Split at first ? or #

  const encodedPath = path.split("/").map(encodeURIComponent).join("/");
  const prefix = extractUrlPrefixFromWebpackConfig();
  let fullUrl = `${encodeURI(prefix)}/${encodedPath}`;

  if (queryAndFragment) {
    fullUrl += queryAndFragment; // preserve ?query=... and/or #fragment
  }

  return fullUrl;
}

// Takes a relative path and generates a URL to the static/ folder.
// Use this for images (i.e. files in the img/ subfolder) and for other
// static resources.
export function toStaticUrl(relativeUrl: string) {
  const trimmed = relativeUrl.trim().replace(/^\//, "");
  const [path, queryAndFragment] = trimmed.split(/(?=[?#])/); // Split at first ? or #

  const encodedPath = path.split("/").map(encodeURIComponent).join("/");
  const assetUrl = `${encodedPath}${queryAndFragment || ""}`;
  const staticFolder = [
    enabledFeatures.elara && "elara/",
    enabledFeatures.embed && "embed/",
    "static",
  ]
    .filter(Boolean)
    .join("");

  return `${encodeURI(getUrlPrefix())}/${staticFolder}/${assetUrl}`;
}

// Currently, the `errorHandler` doesn't really do anything special outside of
// logging to the console. In the Portal, it's defined here:
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/portal-backend/depmap/templates/nav_footer/layout.html#L103
// In Elara, it's defined here:
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/frontend/packages/elara-frontend/webpack.common.js#L37
export const errorHandler: {
  report: (message: string) => void;
} = hasDom
  ? (window as any).errorHandler
  : { report: (message: string) => console.error(message) };

// eslint-disable-next-line @typescript-eslint/ban-types
type DepmapGlobalObject = Record<string, Function> & {
  saveNewContext: (
    context:
      | DataExplorerContextV2
      | {
          dimension_type: string;
          name?: never;
          expr?: never;
          vars?: never;
          contexts?: never;
        },
    onHide?: null | (() => void),
    onSave?: null | ((context: DataExplorerContextV2, hash: string) => void),
    startInTableView?: boolean
  ) => void;

  editContext: (context: DataExplorerContextV2, hash: string) => void;

  repairContext: (
    context: DataExplorerContextV2,
    startInTableView?: boolean
  ) => Promise<DataExplorerContextV2 | null>;
};

// The DepMap global is created by the portal-frontend's Webpack config.
// There is no equivalent in elara-frontend and should be avoided there.
// This is "set" by Webpack as part of its module loader.
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/frontend/packages/portal-frontend/webpack.common.js#L7-L13
// All exported functions in this file become its properties:
// https://github.com/broadinstitute/depmap-portal/blob/a2e2cc9/frontend/packages/portal-frontend/src/index.tsx
export const DepMap =
  hasDom && "Proxy" in window
    ? (new Proxy(
        {},
        {
          get(_, prop) {
            const win = window as any;

            if (win.DepMap && prop in win.DepMap) {
              return win.DepMap[prop];
            }

            const message = [
              `Cannot call \`window.DepMap.${
                prop as string
              }()\` because that function is not defined. `,
              enabledFeatures.elara &&
                "Elara only supports a subset of the global DepMap object's properties.",
              enabledFeatures.embed &&
                "The embeddable frontend does not support the global DepMap object.",
              isPortal &&
                "Only exported functions from " +
                  "frontend/packages/portal-frontend/src/index.tsx " +
                  "are callable.",
            ]
              .filter(Boolean)
              .join("");

            throw new Error(message);
          },
        }
      ) as DepmapGlobalObject)
    : polyfillProxy();

// Proxy has pretty good browser support (https://caniuse.com/proxy) so this
// function is not likely to be needed. But just in case, here's an old school
// fallback. Note that this list of functions can easily go out of sync with
// portal-frontend/src/index.tsx
function polyfillProxy() {
  const proxy = {};

  [
    "log",
    "tailLog",
    "getLogCount",
    "launchContextManagerModal",
    "saveNewContext",
    "editContext",
    "repairContent",
    "initWideTable",
    "initEntitySummary",
    "initSublineagePlot",
  ].forEach((prop) => {
    Object.defineProperty(proxy, prop, {
      get() {
        return (window as any).DepMap[prop];
      },
    });
  });

  return proxy as DepmapGlobalObject;
}
